import { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Divider,
  message,
  Row,
  Col,
  Tag,
  Alert,
  Modal,
  QRCode,
} from 'antd'
import {
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { profileApi } from '@/api/profile'
import type { UpdateProfileInput, ChangePasswordInput } from '@/types/profile'
import { getApiErrorMessage } from '@/utils/error'

const { Title, Text } = Typography

export const Profile = () => {
  const [profileForm] = Form.useForm()
  const [passwordForm] = Form.useForm()
  const [totpForm] = Form.useForm()
  const [setupModalVisible, setSetupModalVisible] = useState(false)
  const [totpSecret, setTotpSecret] = useState('')
  const [totpQRUrl, setTotpQRUrl] = useState('')
  const queryClient = useQueryClient()

  // Fetch profile data
  const { data: profileData, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileApi.getProfile(),
  })

  const profile = profileData?.data.profile

  // Set initial form values when profile loads
  useEffect(() => {
    if (profile) {
      profileForm.setFieldsValue({
        given_name: profile.given_name,
        family_name: profile.family_name,
      })
    }
  }, [profile, profileForm])

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: UpdateProfileInput) => profileApi.updateProfile(data),
    onSuccess: () => {
      message.success('Profile updated successfully')
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to update profile'))
    },
  })

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data: ChangePasswordInput) => profileApi.changePassword(data),
    onSuccess: () => {
      message.success('Password changed successfully')
      passwordForm.resetFields()
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to change password'))
    },
  })

  // Setup TOTP mutation
  const setupTOTPMutation = useMutation({
    mutationFn: () => profileApi.setupTOTP(),
    onSuccess: (response) => {
      setTotpSecret(response.data.secret_code!)
      setTotpQRUrl(response.data.qr_code_url!)
      setSetupModalVisible(true)
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to setup TOTP'))
    },
  })

  // Verify TOTP mutation
  const verifyTOTPMutation = useMutation({
    mutationFn: (totpCode: string) => profileApi.verifyTOTP(totpCode),
    onSuccess: () => {
      message.success('TOTP MFA enabled successfully')
      setSetupModalVisible(false)
      totpForm.resetFields()
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Invalid TOTP code'))
    },
  })

  // Disable MFA mutation
  const disableMFAMutation = useMutation({
    mutationFn: () => profileApi.disableMFA(),
    onSuccess: () => {
      message.success('MFA disabled successfully')
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to disable MFA'))
    },
  })

  const handleUpdateProfile = async (values: UpdateProfileInput) => {
    updateProfileMutation.mutate(values)
  }

  const handleChangePassword = async (values: ChangePasswordInput) => {
    changePasswordMutation.mutate(values)
  }

  const handleSetupTOTP = () => {
    setupTOTPMutation.mutate()
  }

  const handleVerifyTOTP = async (values: { totpCode: string }) => {
    verifyTOTPMutation.mutate(values.totpCode)
  }

  const handleDisableMFA = () => {
    Modal.confirm({
      title: 'Disable MFA',
      content:
        'Are you sure you want to disable multi-factor authentication? This will make your account less secure.',
      okText: 'Disable',
      okType: 'danger',
      onOk: () => {
        disableMFAMutation.mutate()
      },
    })
  }

  if (isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Card loading />
      </div>
    )
  }

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Title level={2}>
        <UserOutlined /> My Profile
      </Title>

      <Row gutter={[24, 24]}>
        {/* Profile Information */}
        <Col xs={24} lg={12}>
          <Card title="Profile Information" bordered={false}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">Email</Text>
                <div>
                  <Text strong>{profile?.email}</Text>
                  {profile?.email_verified && (
                    <Tag color="green" style={{ marginLeft: 8 }}>
                      <CheckCircleOutlined /> Verified
                    </Tag>
                  )}
                </div>
              </div>

              <div>
                <Text type="secondary">Role</Text>
                <div>
                  <Tag color="blue">{profile?.role}</Tag>
                </div>
              </div>

              <Divider />

              <Form form={profileForm} layout="vertical" onFinish={handleUpdateProfile}>
                <Form.Item
                  name="given_name"
                  label="First Name"
                  rules={[{ required: true, message: 'Please enter your first name' }]}
                >
                  <Input prefix={<UserOutlined />} />
                </Form.Item>

                <Form.Item
                  name="family_name"
                  label="Last Name"
                  rules={[{ required: true, message: 'Please enter your last name' }]}
                >
                  <Input prefix={<UserOutlined />} />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={updateProfileMutation.isPending}
                  >
                    Update Profile
                  </Button>
                </Form.Item>
              </Form>
            </Space>
          </Card>
        </Col>

        {/* Password Change */}
        <Col xs={24} lg={12}>
          <Card title="Change Password" bordered={false}>
            <Alert
              message="Password Requirements"
              description="Password must be at least 12 characters and contain uppercase, lowercase, numbers, and special characters."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
              <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[{ required: true, message: 'Please enter your current password' }]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter your new password' },
                  { min: 12, message: 'Password must be at least 12 characters' },
                  {
                    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
                    message:
                      'Password must contain uppercase, lowercase, number, and special character',
                  },
                ]}
                hasFeedback
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['newPassword']}
                hasFeedback
                rules={[
                  { required: true, message: 'Please confirm your new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve()
                      }
                      return Promise.reject(new Error('Passwords do not match!'))
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={changePasswordMutation.isPending}>
                  Change Password
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Multi-Factor Authentication */}
        <Col xs={24}>
          <Card title="Multi-Factor Authentication (MFA)" bordered={false}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Text>
                  Multi-factor authentication adds an extra layer of security to your account by
                  requiring a second verification step when logging in.
                </Text>
              </div>

              <div>
                <Space>
                  <Text strong>MFA Status:</Text>
                  {profile?.mfa_enabled ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      Enabled
                    </Tag>
                  ) : (
                    <Tag color="red" icon={<CloseCircleOutlined />}>
                      Disabled
                    </Tag>
                  )}
                </Space>
              </div>

              <Divider />

              {!profile?.mfa_enabled ? (
                <Space direction="vertical" size="middle">
                  <Title level={5}>
                    <SafetyOutlined /> Setup Authenticator App (TOTP)
                  </Title>
                  <Text>
                    Use an authenticator app like Google Authenticator, Authy, or Microsoft
                    Authenticator to generate verification codes.
                  </Text>
                  <Button
                    type="primary"
                    onClick={handleSetupTOTP}
                    loading={setupTOTPMutation.isPending}
                  >
                    Setup Authenticator App
                  </Button>
                </Space>
              ) : (
                <Space direction="vertical" size="middle">
                  <Alert
                    message="MFA is Active"
                    description="Your account is protected with multi-factor authentication."
                    type="success"
                    showIcon
                  />
                  <Button danger onClick={handleDisableMFA} loading={disableMFAMutation.isPending}>
                    Disable MFA
                  </Button>
                </Space>
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      {/* TOTP Setup Modal */}
      <Modal
        title="Setup Authenticator App"
        open={setupModalVisible}
        onCancel={() => {
          setSetupModalVisible(false)
          totpForm.resetFields()
        }}
        footer={null}
        width={600}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert message="Follow these steps to enable MFA" type="info" showIcon />

          <div>
            <Title level={5}>Step 1: Scan QR Code</Title>
            <Text>Open your authenticator app and scan this QR code:</Text>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              {totpQRUrl && <QRCode value={totpQRUrl} size={200} />}
            </div>
          </div>

          <div>
            <Title level={5}>Step 2: Or Enter Secret Manually</Title>
            <Text>If you can't scan the QR code, enter this secret in your app:</Text>
            <div style={{ marginTop: 8 }}>
              <Text code copyable>
                {totpSecret}
              </Text>
            </div>
          </div>

          <div>
            <Title level={5}>Step 3: Verify Code</Title>
            <Text>Enter the 6-digit code from your authenticator app:</Text>
            <Form
              form={totpForm}
              layout="vertical"
              onFinish={handleVerifyTOTP}
              style={{ marginTop: 16 }}
            >
              <Form.Item
                name="totpCode"
                rules={[
                  { required: true, message: 'Please enter the 6-digit code' },
                  { len: 6, message: 'Code must be exactly 6 digits' },
                  { pattern: /^\d+$/, message: 'Code must contain only numbers' },
                ]}
              >
                <Input
                  placeholder="000000"
                  maxLength={6}
                  size="large"
                  style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8 }}
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={verifyTOTPMutation.isPending}>
                    Verify and Enable
                  </Button>
                  <Button
                    onClick={() => {
                      setSetupModalVisible(false)
                      totpForm.resetFields()
                    }}
                  >
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        </Space>
      </Modal>
    </div>
  )
}
